import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Loader2, MessageCircle, Mail, Phone, MapPin,
  CalendarIcon, ImagePlus, X as XIcon, Send,
} from "lucide-react";
import { SITE, telLink, mailLink, waLink } from "@/lib/site";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const initial = {
  name: "", phone: "", email: "", postcode: "",
  moving_from: "", moving_to: "", preferred_date: "",
  services: "House Removal", message: "",
};

const serviceOptions = [
  "House Removal", "Flat / Apartment Move", "Student Move", "Office Removal",
  "Packers & Movers (Full-Service)", "Packing Service", "End of Tenancy Cleaning",
  "Deep Cleaning", "Man and Van", "Furniture Assembly", "Other",
];

const MAX_PHOTOS = 8;
const MAX_MB = 10;

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [photos, setPhotos] = useState([]); // [{url, previewUrl, filename, uploading}]
  const fileInputRef = useRef(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSelectDate = (d) => {
    setSelectedDate(d);
    setForm({ ...form, preferred_date: d ? format(d, "yyyy-MM-dd") : "" });
    setDateOpen(false);
  };

  const onSelectFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`You can attach up to ${MAX_PHOTOS} photos.`);
      return;
    }
    e.target.value = ""; // reset input

    for (const file of files) {
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${file.name} is over ${MAX_MB}MB.`);
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      const tempId = Math.random().toString(36).slice(2);
      setPhotos((p) => [...p, { tempId, previewUrl, uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => p.map((x) => x.tempId === tempId ? { ...x, url: data.url, filename: data.filename, uploading: false } : x));
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
        setPhotos((p) => p.filter((x) => x.tempId !== tempId));
      }
    }
  };

  const removePhoto = (tempId) => {
    setPhotos((p) => p.filter((x) => x.tempId !== tempId));
  };

  const buildWaMessage = (f, urls) => {
    const lines = [
      `Hello BoxB Solutions, I would like a quotation.`,
      ``,
      `Name: ${f.name}`,
      `Phone: ${f.phone}`,
      `Email: ${f.email}`,
      f.postcode && `Postcode: ${f.postcode}`,
      f.moving_from && `Moving From: ${f.moving_from}`,
      f.moving_to && `Moving To: ${f.moving_to}`,
      f.preferred_date && `Preferred Date: ${f.preferred_date}`,
      f.services && `Services: ${f.services}`,
      f.message && `Message: ${f.message}`,
    ].filter(Boolean);
    if (urls.length) {
      lines.push("");
      lines.push(`Photos (${urls.length}):`);
      urls.forEach((u, i) => lines.push(`${i + 1}. ${u}`));
    }
    return lines.join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    if (photos.some((p) => p.uploading)) {
      toast.error("Please wait — photos still uploading.");
      return;
    }
    setSubmitting(true);
    try {
      const photo_urls = photos.filter((p) => p.url).map((p) => p.url);
      await api.post(`/enquiries`, { ...form, photo_urls });
      toast.success("Thanks! We'll be in touch shortly. Opening WhatsApp with your details…");
      const url = waLink(buildWaMessage(form, photo_urls));
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), 400);
      setForm(initial);
      setSelectedDate(null);
      setPhotos([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">Get a Free Quote</span>
          <h2 className="mt-4 font-display font-semibold text-[#0A192F] text-4xl sm:text-5xl tracking-tight">
            Tell us about your move.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Fill out the form — you can also <span className="font-semibold text-[#0A192F]">upload photos</span> of items or rooms so we can give you a more accurate quote. Your details will open in WhatsApp so you can send them to us in one tap.
          </p>

          <div className="mt-10 space-y-4">
            <a href={telLink} data-testid="contact-call" className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 hover-lift">
              <span className="h-11 w-11 rounded-2xl bg-[#0A192F] text-white inline-flex items-center justify-center"><Phone className="h-5 w-5" /></span>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Call Now</div><div className="font-semibold text-[#0A192F]">{SITE.phone}</div></div>
            </a>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 hover-lift">
              <span className="h-11 w-11 rounded-2xl bg-[#25D366] text-white inline-flex items-center justify-center"><MessageCircle className="h-5 w-5" /></span>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-bold">WhatsApp</div><div className="font-semibold text-[#0A192F]">{SITE.phone}</div></div>
            </a>
            <a href={mailLink} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4 hover-lift">
              <span className="h-11 w-11 rounded-2xl bg-[#2563EB] text-white inline-flex items-center justify-center"><Mail className="h-5 w-5" /></span>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Email Enquiry</div><div className="font-semibold text-[#0A192F]">{SITE.email}</div></div>
            </a>
            <div className="flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-4">
              <span className="h-11 w-11 rounded-2xl bg-slate-100 text-[#0A192F] inline-flex items-center justify-center"><MapPin className="h-5 w-5" /></span>
              <div><div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Based in</div><div className="font-semibold text-[#0A192F]">{SITE.location}</div></div>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
            <iframe
              title="BoxB Solutions - Cardiff service area map"
              src="https://www.google.com/maps?q=Cardiff,%20South%20Wales,%20UK&output=embed"
              width="100%" height="240" loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block"
            />
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="enquiry-form"
              className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 p-6 sm:p-10 shadow-[0_20px_60px_rgb(10,25,47,0.05)]">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name*" name="name" value={form.name} onChange={onChange} testid="input-name" placeholder="Your full name" />
            <Field label="Phone*" name="phone" value={form.phone} onChange={onChange} testid="input-phone" placeholder="+44 …" />
            <Field label="Email*" name="email" type="email" value={form.email} onChange={onChange} testid="input-email" placeholder="you@example.com" />
            <Field label="Postcode" name="postcode" value={form.postcode} onChange={onChange} testid="input-postcode" placeholder="CF10 …" />
            <Field label="Moving From" name="moving_from" value={form.moving_from} onChange={onChange} testid="input-from" placeholder="Address or area" />
            <Field label="Moving To" name="moving_to" value={form.moving_to} onChange={onChange} testid="input-to" placeholder="Address or area" />

            {/* Calendar picker */}
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Preferred Date</label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="input-date-trigger"
                    className={`w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left flex items-center justify-between hover:border-[#0A192F] focus:border-[#0A192F] focus:ring-2 focus:ring-[#0A192F]/10 outline-none transition-colors ${selectedDate ? "text-[#0A192F]" : "text-slate-400"}`}
                  >
                    <span className="font-medium">
                      {selectedDate ? format(selectedDate, "EEE, d MMM yyyy") : "Select a date"}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0" data-testid="date-popover">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={onSelectDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Services Required</label>
              <select data-testid="input-services" name="services" value={form.services} onChange={onChange}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[#0A192F] focus:border-[#0A192F] focus:ring-2 focus:ring-[#0A192F]/10 outline-none">
                {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">Message</label>
            <textarea data-testid="input-message" name="message" rows={4} value={form.message} onChange={onChange}
                      placeholder="Tell us a bit about your move — property size, floor, items, access, timing…"
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[#0A192F] focus:border-[#0A192F] focus:ring-2 focus:ring-[#0A192F]/10 outline-none resize-none" />
          </div>

          {/* Photo upload */}
          <div className="mt-5">
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
              Photos (optional) <span className="text-slate-400 font-normal normal-case tracking-normal">— up to {MAX_PHOTOS} · max {MAX_MB}MB each</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onSelectFiles}
              className="hidden"
              data-testid="input-photos"
            />
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {photos.map((p) => (
                <div key={p.tempId} data-testid={`photo-thumb-${p.tempId}`} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                  <img src={p.previewUrl} alt="Upload preview" className="h-full w-full object-cover" />
                  {p.uploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 text-white animate-spin" />
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label="Remove photo"
                    data-testid={`photo-remove-${p.tempId}`}
                    onClick={() => removePhoto(p.tempId)}
                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  data-testid="add-photos-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-[#0A192F] flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-[#0A192F] transition-colors"
                >
                  <ImagePlus className="h-6 w-6" />
                  <span className="text-xs font-semibold">Add photos</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button type="submit" disabled={submitting} data-testid="submit-enquiry-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white px-8 py-4 font-semibold hover:bg-[#0F172A] transition-all hover:shadow-lg disabled:opacity-70">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send Enquiry</>}
            </button>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-form-wa"
               className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-8 py-4 font-semibold hover:brightness-95 transition-all">
              <MessageCircle className="h-4 w-4" /> Or WhatsApp Us
            </a>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            By submitting, you agree to be contacted by BoxB Solutions regarding your enquiry. Photos are used only to prepare your quote.
          </p>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, testid, type = "text", placeholder }) => (
  <div>
    <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">{label}</label>
    <input data-testid={testid} type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
           className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[#0A192F] placeholder:text-slate-400 focus:border-[#0A192F] focus:ring-2 focus:ring-[#0A192F]/10 outline-none" />
  </div>
);
